/**
 * Context Menu for Chat Bubbles
 *
 * Mobile (touch): iOS-style blur backdrop with elevated bubble clone,
 *                 emoji reaction bar, and action menu on long-press.
 * Desktop:        Inline overlay dropdown (same pattern as attachment menu)
 *                 on right-click, anchored to the message bubble.
 */
import { createInlineOverlay, activeOverlays, isTouchDevice } from "./utils";
import { toggleReaction, findUserReaction, showReactionPicker } from "./reactions";

const LONG_PRESS_MS = 400;
const MOVE_TOLERANCE = 10;
const QUICK_REACTIONS = ["❤️", "😂", "👍", "😮", "😢", "🙏"];

// ── Public API ──────────────────────────────────────────────

/**
 * Attach long-press (mobile) + right-click (desktop) listeners to a
 * message wrapper element.
 */
export function attachContextMenu(wrapper, { event, client, chatUI, isOwn }) {
  let timer = null;
  let startX = 0;
  let startY = 0;
  let moved = false;

  const bubble = wrapper.querySelector(".bryght-message-bubble");
  if (!bubble) return;

  // ── Touch (mobile) — iOS-style full overlay ──
  bubble.addEventListener("touchstart", (e) => {
    if (e.target.closest("a, button, audio, video, input, textarea")) return;

    moved = false;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;

    timer = setTimeout(() => {
      if (!moved) {
        // Whatever iOS selected while the finger was held down: the overlay
        // is about to open on top of it. (preventDefault on the touchstart
        // would be a no-op this late — the event has long been dispatched.)
        window.getSelection()?.removeAllRanges();
        if (navigator.vibrate) navigator.vibrate(10);
        showMobileContextMenu({ event, client, chatUI, isOwn, bubble });
      }
    }, LONG_PRESS_MS);
  }, { passive: true });

  bubble.addEventListener("touchmove", (e) => {
    if (!timer) return;
    const dx = Math.abs(e.touches[0].clientX - startX);
    const dy = Math.abs(e.touches[0].clientY - startY);
    if (dx > MOVE_TOLERANCE || dy > MOVE_TOLERANCE) {
      moved = true;
      clearTimeout(timer);
      timer = null;
    }
  }, { passive: true });

  bubble.addEventListener("touchend", () => {
    clearTimeout(timer);
    timer = null;
  });

  bubble.addEventListener("touchcancel", () => {
    clearTimeout(timer);
    timer = null;
  });

  // ── Right-click (desktop) — inline overlay dropdown ──
  bubble.addEventListener("contextmenu", (e) => {
    if (e.target.closest("a, button, audio, video, input, textarea")) return;
    e.preventDefault();

    if (isTouchDevice()) {
      // On touch devices that also fire contextmenu (e.g. some Android),
      // fall through to the mobile overlay instead.
      showMobileContextMenu({ event, client, chatUI, isOwn, bubble });
    } else {
      showDesktopContextMenu({ event, client, chatUI, isOwn, bubble });
    }
  });
}

// ═════════════════════════════════════════════════════════════
// Desktop: inline overlay (same pattern as attachment menu)
// ═════════════════════════════════════════════════════════════

function showDesktopContextMenu({ event, client, chatUI, isOwn, bubble }) {
  // Close any existing message context overlay
  if (activeOverlays.has("message-context")) {
    activeOverlays.get("message-context").close();
  }

  const roomId = event.getRoomId();
  const eventId = event.getId();
  const content = event.getContent();

  const menu = document.createElement("div");
  menu.className = "bryght-attachment-menu";

  const overlay = createInlineOverlay({
    anchor: bubble,
    content: menu,
    group: "message-context",
    align: isOwn ? "right" : "left",
    gap: 6,
  });

  if (!overlay) return;

  // ── Emoji react ──
  const btnReact = document.createElement("button");
  btnReact.className = "bryght-attachment-btn";
  btnReact.innerHTML = `<span class="icon">${emojiIcon()}</span> ${chatUI.t("react", "Reagieren")}`;
  btnReact.onclick = () => {
    overlay.close();
    showReactionPicker(roomId, eventId, client, bubble.querySelector(".bryght-react-btn") || bubble);
  };
  menu.appendChild(btnReact);

  // ── Reply ──
  const btnReply = document.createElement("button");
  btnReply.className = "bryght-attachment-btn";
  btnReply.innerHTML = `<span class="icon">${replyIcon()}</span> ${chatUI.t("reply", "Antworten")}`;
  btnReply.onclick = () => {
    overlay.close();
    chatUI.showReplyContext(event);
    const input = document.querySelector(".bryght-composer-input");
    if (input) input.focus();
  };
  menu.appendChild(btnReply);

  // ── Copy (text messages only) ──
  if (content.body && (content.msgtype === "m.text" || content.msgtype === "m.notice")) {
    const btnCopy = document.createElement("button");
    btnCopy.className = "bryght-attachment-btn";
    btnCopy.innerHTML = `<span class="icon">${copyIcon()}</span> ${chatUI.t("copy", "Kopieren")}`;
    btnCopy.onclick = async () => {
      overlay.close();
      try {
        await navigator.clipboard.writeText(content.body);
      } catch (_) {
        const ta = document.createElement("textarea");
        ta.value = content.body;
        ta.style.cssText = "position:fixed;opacity:0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
    };
    menu.appendChild(btnCopy);
  }

  // ── Delete (own messages only) ──
  if (isOwn) {
    const btnDelete = document.createElement("button");
    btnDelete.className = "bryght-attachment-btn bryght-attachment-btn--danger";
    btnDelete.innerHTML = `<span class="icon">${deleteIcon()}</span> ${chatUI.t("delete_message", "Löschen")}`;
    btnDelete.onclick = async () => {
      overlay.close();
      try {
        await client.redactEvent(roomId, eventId);
      } catch (err) {
        console.error("[BryghtChat] Delete failed:", err);
      }
    };
    menu.appendChild(btnDelete);
  }
}

// ═════════════════════════════════════════════════════════════
// Mobile: iOS-style full-screen overlay
// ═════════════════════════════════════════════════════════════

let activeOverlayMobile = null;

function closeMobileOverlay() {
  if (!activeOverlayMobile) return;
  const { backdrop, container } = activeOverlayMobile;

  container.classList.add("bryght-context-overlay--closing");
  backdrop.classList.add("bryght-context-backdrop--closing");

  const onEnd = () => {
    backdrop.remove();
    container.remove();
    activeOverlayMobile = null;
  };
  container.addEventListener("animationend", onEnd, { once: true });
  setTimeout(onEnd, 300);
}

function showMobileContextMenu({ event, client, chatUI, isOwn, bubble }) {
  if (activeOverlayMobile) closeMobileOverlay();

  const roomId = event.getRoomId();
  const eventId = event.getId();
  const content = event.getContent();

  // ── 1. Backdrop ──
  const backdrop = document.createElement("div");
  backdrop.className = "bryght-context-backdrop";
  backdrop.addEventListener("click", () => closeMobileOverlay());

  // ── 2. Container ──
  const container = document.createElement("div");
  container.className = "bryght-context-overlay";

  // ── 3. Emoji reaction bar ──
  const reactionBar = document.createElement("div");
  reactionBar.className = "bryght-context-reactions";

  const currentReaction = findUserReaction(client, roomId, eventId);

  QUICK_REACTIONS.forEach((emoji) => {
    const btn = document.createElement("button");
    btn.className = "bryght-context-reactions__btn";
    if (currentReaction?.emoji === emoji) {
      btn.classList.add("bryght-context-reactions__btn--active");
    }
    btn.textContent = emoji;
    btn.addEventListener("click", async () => {
      closeMobileOverlay();
      await toggleReaction(client, roomId, eventId, emoji);
    });
    reactionBar.appendChild(btn);
  });

  const moreBtn = document.createElement("button");
  moreBtn.className = "bryght-context-reactions__btn bryght-context-reactions__more";
  moreBtn.textContent = "+";
  moreBtn.addEventListener("click", () => {
    closeMobileOverlay();
    const reactBtn = bubble.querySelector(".bryght-react-btn");
    if (reactBtn) reactBtn.click();
  });
  reactionBar.appendChild(moreBtn);
  container.appendChild(reactionBar);

  // ── 4. Cloned bubble ──
  const bubbleClone = bubble.cloneNode(true);
  bubbleClone.className = "bryght-context-bubble";
  if (isOwn) bubbleClone.classList.add("bryght-context-bubble--own");
  const clonedReactBtn = bubbleClone.querySelector(".bryght-react-btn");
  if (clonedReactBtn) clonedReactBtn.remove();
  const clonedReactions = bubbleClone.querySelector(".bryght-reactions");
  if (clonedReactions) clonedReactions.remove();
  container.appendChild(bubbleClone);

  // ── 5. Action menu ──
  const menu = document.createElement("div");
  menu.className = "bryght-context-menu";

  menu.appendChild(createMenuItem({
    icon: replyIcon(),
    label: chatUI.t("reply", "Antworten"),
    onClick: () => {
      closeMobileOverlay();
      chatUI.showReplyContext(event);
      const input = document.querySelector(".bryght-composer-input");
      if (input) input.focus();
    },
  }));

  if (content.body && (content.msgtype === "m.text" || content.msgtype === "m.notice")) {
    menu.appendChild(createMenuItem({
      icon: copyIcon(),
      label: chatUI.t("copy", "Kopieren"),
      onClick: async () => {
        closeMobileOverlay();
        try {
          await navigator.clipboard.writeText(content.body);
        } catch (_) {
          const ta = document.createElement("textarea");
          ta.value = content.body;
          ta.style.cssText = "position:fixed;opacity:0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          ta.remove();
        }
      },
    }));
  }

  if (isOwn) {
    menu.appendChild(createMenuItem({
      icon: deleteIcon(),
      label: chatUI.t("delete_message", "Löschen"),
      danger: true,
      onClick: async () => {
        closeMobileOverlay();
        try {
          await client.redactEvent(roomId, eventId);
        } catch (err) {
          console.error("[BryghtChat] Delete failed:", err);
        }
      },
    }));
  }

  container.appendChild(menu);

  // ── Mount ──
  document.body.appendChild(backdrop);
  document.body.appendChild(container);
  activeOverlayMobile = { backdrop, container };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      closeMobileOverlay();
      document.removeEventListener("keydown", onKeyDown);
    }
  };
  document.addEventListener("keydown", onKeyDown);
}

// ── Helpers ─────────────────────────────────────────────────

function createMenuItem({ icon, label, danger = false, onClick }) {
  const item = document.createElement("button");
  item.className = "bryght-context-menu__item";
  if (danger) item.classList.add("bryght-context-menu__item--danger");

  const iconEl = document.createElement("span");
  iconEl.className = "bryght-context-menu__icon";
  iconEl.innerHTML = icon;
  item.appendChild(iconEl);

  const labelEl = document.createElement("span");
  labelEl.className = "bryght-context-menu__label";
  labelEl.textContent = label;
  item.appendChild(labelEl);

  item.addEventListener("click", onClick);
  return item;
}

function emojiIcon() {
  return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>`;
}

function replyIcon() {
  return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>`;
}

function copyIcon() {
  return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
}

function deleteIcon() {
  return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;
}
